/**
 * EmailStatusBadge — Tiny indicator for the Admin Dashboard.
 *
 * Polls `GET /api/admin/email-status` once on mount and shows ONE of:
 *   ✓ Email Status: Verified  (green, when domain or sender is authenticated)
 *   ⚠ Email Status: Not Verified  (amber, with a one-line warning)
 *
 * Intentionally minimal — no charts, no actions, no auto-refresh.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailStatusBadge() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/email-status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!cancelled) setState({ loading: false, data: res.data });
      } catch {
        if (!cancelled) setState({ loading: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full"
        data-testid="email-status-badge-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
        Email status…
      </span>
    );
  }
  if (!state.data) return null;
  const { ok, from_email, warning } = state.data;
  if (ok) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full"
        title={`Sender: ${from_email}`}
        data-testid="email-status-badge-verified"
      >
        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
        Email Status: Verified
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 px-2.5 py-1 bg-amber-50 border border-amber-300 rounded-full"
      title={warning || "Domain not authenticated"}
      data-testid="email-status-badge-not-verified"
    >
      <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
      Email Status: Not Verified
    </span>
  );
}
